import React, { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { bytesToString } from 'js/lib/FormatUtils';
import { MetricRow } from '../metricRows';
import { getAdaptiveTicks } from '../chartTicks';
import { extractTooltipRow, RechartsTooltipPayload } from './MetricsCombinedChart';
import { VOLUME_COLOR } from './chartColors';
import styles from './MetricsCombinedChart.module.scss';

export { VOLUME_COLOR };

export function formatVolumeBytes(value: number): string {
  return bytesToString(value) || '0 B';
}

const VolumeTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: RechartsTooltipPayload;
}) => {
  const row = extractTooltipRow(active, payload);
  if (!row) return null;
  return (
    <div className={styles.tooltipContainer}>
      <span className={styles.tooltipDate}>{row.date}</span>
      <span className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Data Volume</span>
        <span className={styles.tooltipValue}>{formatVolumeBytes(row.size)}</span>
      </span>
    </div>
  );
};

export type MetricsVolumeChartProps = {
  rows: MetricRow[];
  height?: number;
};

export const MetricsVolumeChart = (props: MetricsVolumeChartProps) => {
  const { rows, height = 300 } = props;

  const ticks = useMemo(() => getAdaptiveTicks(rows), [rows]);

  const tooltipContent = useMemo(() => <VolumeTooltip />, []);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="label" ticks={ticks} interval="preserveStartEnd" />
        <YAxis orientation="left" tickFormatter={formatVolumeBytes} width={70} />

        <Tooltip
          cursor={{ fill: 'var(--color-greyscale-100)' }}
          isAnimationActive={false}
          content={tooltipContent}
        />

        <Area type="monotone" dataKey="size" fill={VOLUME_COLOR} fillOpacity={0.15} stroke="none" />
        <Line type="monotone" dataKey="size" stroke={VOLUME_COLOR} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

MetricsVolumeChart.displayName = 'MetricsVolumeChart';
